import { useEffect, useRef, useState } from 'react';
import { EmblemAnimated, LogoAnimated, useReducedMotion } from '@nula/core-ui';
import { NulaLockup } from '@/components/nula-lockup/nula-lockup';
import { NulaLogo } from '@/components/nula-logo/nula-logo';
import styles from './sidebar-brand.module.css';

interface SidebarBrandProps {
  collapsed: boolean;
}

/** Base hover-tuned draw duration (the collapsed orb), still snappier than the
 *  login/splash draw (~3340ms). */
export const HOVER_DRAW_MS = 1400;

/** The expanded lockup draws slower than the orb (2.5s) — more glyphs to reveal,
 *  and a calmer pace reads better on the wordmark. */
export const LOCKUP_DRAW_MS = 2500;

/** Extra time after the draw before removing the overlay (stroke→fill settle). */
const SETTLE_MS = 1100;

/** The expanded static lockup's rendered width (height 40 × ~2.9 aspect). */
const LOCKUP_WIDTH = 116;

/** LogoAnimated's viewBox (580 wide) pads the glyphs the static lockup's viewBox
 *  (504 wide) doesn't, so the animated box must be scaled up by 580/504 for its
 *  glyphs to render at the same size as the static lockup. */
const ANIMATED_LOCKUP_SIZE = Math.round(LOCKUP_WIDTH * (580 / 504));

/**
 * The sidebar brand mark. The static logo (orb when collapsed, full lockup when
 * expanded) is ALWAYS mounted — it holds the layout box and is the rest state,
 * so there's no size jump. Hovering overlays the login-screen stroke-draw
 * animation on top (absolutely positioned, same box), then removes it when the
 * draw finishes — replaying on each fresh hover. A re-hover mid-draw is ignored
 * (debounced) so it never restarts halfway. Honors `prefers-reduced-motion`.
 */
export function SidebarBrand({ collapsed }: SidebarBrandProps) {
  const shouldReduce = useReducedMotion();
  const [drawId, setDrawId] = useState(0); // >0 while an overlay draw is mounted
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  // The expanded lockup draws slower than the collapsed orb.
  const drawMs = collapsed ? HOVER_DRAW_MS : LOCKUP_DRAW_MS;

  const handleMouseEnter = () => {
    if (shouldReduce) return;
    if (drawId !== 0) return; // a draw is already in flight — ignore (debounce)
    setDrawId((id) => id + 1);
    timerRef.current = setTimeout(() => setDrawId(0), drawMs + SETTLE_MS);
  };

  const drawing = drawId > 0;

  return (
    <div
      className={styles.brand}
      onMouseEnter={handleMouseEnter}
      data-drawing={drawing ? 'true' : undefined}
      data-testid="sidebar-brand"
    >
      {/* Static logo — always mounted: it's the rest state AND holds the layout
          box. While drawing it's hidden (visibility, not display) so the box is
          kept but only the animated overlay shows — no double/ghosted logo. */}
      <div className={styles.staticLayer}>
        {collapsed ? <NulaLogo size={32} /> : <NulaLockup height={40} className={styles.lockup} />}
      </div>

      {/* Animated draw — overlaid on top while a hover draw is in flight. Keyed
          by drawId so each fresh hover remounts (replays) the draw. */}
      {drawing && (
        <div className={styles.overlay} aria-hidden="true">
          {collapsed ? (
            <EmblemAnimated key={drawId} size={32} loop={false} duration={HOVER_DRAW_MS} />
          ) : (
            <LogoAnimated key={drawId} size={ANIMATED_LOCKUP_SIZE} loop={false} duration={LOCKUP_DRAW_MS} />
          )}
        </div>
      )}
    </div>
  );
}
